import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Send, BellRing, KeyRound, Image as ImageIcon } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";

const FIELDS = [
  ["business_name", "Company Name"],
  ["phone", "Main Phone"],
  ["secondary_phone", "Secondary Phone"],
  ["email", "Business Email"],
  ["service_area", "Service Area"],
  ["business_hours", "Business Hours"],
  ["facebook_url", "Facebook URL"],
  ["nextdoor_url", "Nextdoor URL"],
  ["google_business_url", "Google Business Profile URL"],
  ["announcement", "Website Announcement (banner)"],
];

const SUPER_FIELDS = [
  ["yosi_url", "Yosi Media Studio Link"],
  ["ga4_measurement_id", "Google Analytics 4 Measurement ID"],
];

export default function PortalSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [tgBusy, setTgBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const logoRef = useRef(null);
  const badgeRef = useRef(null);
  const heroRef0 = useRef(null);
  const heroRef1 = useRef(null);
  const { setSettings, user } = useSite();
  const isSuper = user?.role === "super_admin";

  useEffect(() => { api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);

  if (!form) return <p className="text-slate-400">Loading…</p>;

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/admin/upload", fd);
      const updated = { ...form, logo_url: data.urls[0] };
      setForm(updated);
      const res = await api.put("/admin/settings", updated);
      setSettings(res.data);
      toast.success("Official logo uploaded — now used across the website and portal");
    } catch { toast.error("Logo upload failed. Use JPG, PNG or WEBP up to 10MB."); }
    setLogoBusy(false);
  };

  const removeLogo = async () => {
    const updated = { ...form, logo_url: "" };
    setForm(updated);
    const res = await api.put("/admin/settings", updated);
    setSettings(res.data);
    toast.success("Logo removed — default emblem restored");
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setSettings(data);
      toast.success("Settings saved — website updated");
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const uploadBadge = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/admin/upload", fd);
      const updated = { ...form, nextdoor_badge_url: data.urls[0] };
      setForm(updated);
      const res = await api.put("/admin/settings", updated);
      setSettings(res.data);
      toast.success("Nextdoor badge uploaded — shown on the public Reviews page");
    } catch { toast.error("Badge upload failed"); }
  };

  const detectTelegram = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/admin/telegram/detect", { token: form.telegram_bot_token || "" });
      const fresh = await api.get("/admin/settings");
      setForm(fresh.data);
      toast.success(`Connected to Telegram chat: ${data.chat_name}. Send a test to confirm!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Detection failed");
    }
    setTgBusy(false);
  };

  const testTelegram = async () => {
    setTgBusy(true);
    try {
      await api.post("/admin/telegram/test");
      toast.success("Test message sent — check your Telegram!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    }
    setTgBusy(false);
  };

  const saveNow = async (updated) => {
    setForm(updated);
    const res = await api.put("/admin/settings", updated);
    setSettings(res.data);
  };

  const uploadHero = async (file, idx) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/admin/upload", fd);
      const imgs = [...(form.hero_images || [])];
      imgs[idx] = data.urls[0];
      await saveNow({ ...form, hero_images: imgs.filter(Boolean) });
      toast.success("Cover photo saved — homepage updated");
    } catch { toast.error("Upload failed. Use JPG, PNG or WEBP up to 10MB."); }
  };

  const removeHero = async (idx) => {
    const imgs = (form.hero_images || []).filter((_, i) => i !== idx);
    await saveNow({ ...form, hero_images: imgs });
    toast.success("Cover photo removed");
  };

  const changePassword = async () => {
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed successfully");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Password change failed");
    }
    setPwBusy(false);
  };

  return (
    <div data-testid="portal-settings" className="max-w-3xl">
      <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl mb-8">Website Settings</h1>
      <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6" data-testid="change-password-card">
        <h2 className="font-heading font-bold text-[#0B1B3D] mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#005AE0]" /> Change My Password / Cambiar mi contraseña</h2>
        <p className="text-sm text-slate-500 mb-5">Signed in as <strong>{user?.email}</strong></p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="field-label">Current Password</label>
            <input className="field" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current-input" /></div>
          <div><label className="field-label">New Password (min. 8)</label>
            <input className="field" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new-input" /></div>
          <div><label className="field-label">Confirm New Password</label>
            <input className="field" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm-input" /></div>
        </div>
        <button onClick={changePassword} disabled={pwBusy || !pw.current || !pw.next} className="btn-primary !py-2.5 !px-5 !text-xs mt-5" data-testid="pw-change-btn">
          {pwBusy ? "Saving…" : "Change Password"}
        </button>
      </div>
      <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6" data-testid="hero-settings-card">
        <h2 className="font-heading font-bold text-[#0B1B3D] mb-2 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#005AE0]" /> Homepage Cover Photos (Hero)</h2>
        <p className="text-sm text-slate-500 mb-5">Upload up to 2 real photos for the homepage cover. With 2 photos the cover rotates automatically every few seconds. Use wide/horizontal photos for best quality. If empty, the default cover is used.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[0, 1].map((idx) => {
            const img = (form.hero_images || [])[idx];
            const ref = idx === 0 ? heroRef0 : heroRef1;
            return (
              <div key={idx}>
                <label className="field-label">Cover Photo {idx + 1}</label>
                {img ? (
                  <img src={imgUrl(img)} alt={`Cover ${idx + 1}`} className="w-full h-44 object-cover border border-slate-200 mb-3" data-testid={`hero-preview-${idx}`} />
                ) : (
                  <div className="w-full h-44 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-sm mb-3">No photo yet</div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => ref.current?.click()} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid={`hero-upload-btn-${idx}`}>
                    <Upload className="w-4 h-4" /> {img ? "Replace" : "Upload"}
                  </button>
                  {img && (
                    <button onClick={() => removeHero(idx)} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid={`hero-remove-btn-${idx}`}>
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
                <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp,.heic" className="hidden" data-testid={`hero-file-input-${idx}`}
                  onChange={(e) => uploadHero(e.target.files[0], idx)} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6" data-testid="logo-settings-card">
        <h2 className="font-heading font-bold text-[#0B1B3D] mb-2">Official Company Logo</h2>
        <p className="text-sm text-slate-500 mb-5">Upload your official logo (JPG, PNG or WEBP). It will automatically replace the default emblem in the header, footer, portal and favicon — keeping its original proportions.</p>
        <div className="flex flex-wrap items-center gap-5">
          <div className="h-20 min-w-[120px] px-4 bg-[#0B1B3D] flex items-center justify-center">
            {form.logo_url ? (
              <img src={imgUrl(form.logo_url)} alt="Official logo" className="max-h-16 w-auto object-contain" data-testid="logo-preview" />
            ) : (
              <span className="text-slate-400 text-xs font-heading">No logo uploaded</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => logoRef.current?.click()} disabled={logoBusy} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="logo-upload-btn">
              <Upload className="w-4 h-4" /> {logoBusy ? "Uploading…" : form.logo_url ? "Replace Logo" : "Upload Logo"}
            </button>
            {form.logo_url && (
              <button onClick={removeLogo} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="logo-remove-btn">
                <Trash2 className="w-4 h-4" /> Remove
              </button>
            )}
          </div>
          <input ref={logoRef} type="file" accept=".jpg,.jpeg,.png,.webp,.svg" className="hidden" data-testid="logo-file-input"
            onChange={(e) => uploadLogo(e.target.files[0])} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6" data-testid="telegram-settings-card">
        <h2 className="font-heading font-bold text-[#0B1B3D] mb-2 flex items-center gap-2"><BellRing className="w-4 h-4 text-[#005AE0]" /> Instant Lead Alerts on Your Phone (Telegram — Free)</h2>
        <ol className="text-sm text-slate-500 mb-5 list-decimal pl-5 space-y-1">
          <li>Install the free <strong>Telegram</strong> app and search for <strong>@BotFather</strong>.</li>
          <li>Send it <strong>/newbot</strong>, follow the steps and copy the <strong>token</strong> it gives you.</li>
          <li>Paste the token below, then open your new bot in Telegram and send it <strong>/start</strong>.</li>
          <li>Click <strong>Connect</strong> and then <strong>Send Test</strong>.</li>
        </ol>
        <div className="flex flex-wrap gap-3 items-center">
          <input className="field !h-11 flex-1 min-w-[260px]" placeholder="Bot token from @BotFather (e.g. 123456:ABC-DEF…)"
            value={form.telegram_bot_token || ""} onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
            data-testid="telegram-token-input" />
          <button onClick={detectTelegram} disabled={tgBusy} className="btn-primary !py-2.5 !px-4 !text-xs" data-testid="telegram-connect-btn">
            {tgBusy ? "Working…" : "Connect"}
          </button>
          <button onClick={testTelegram} disabled={tgBusy || !form.telegram_chat_id} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="telegram-test-btn">
            <Send className="w-4 h-4" /> Send Test
          </button>
        </div>
        <p className="text-xs mt-3 font-semibold" data-testid="telegram-status">
          {form.telegram_chat_id
            ? <span className="text-green-600">✓ Connected — new estimate requests will arrive instantly on Telegram</span>
            : <span className="text-slate-400">Not connected yet</span>}
        </p>
      </div>
      <div className="bg-white border border-slate-200 p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[...FIELDS, ...(isSuper ? SUPER_FIELDS : [])].map(([k, label]) => (
            <div key={k} className={k === "announcement" ? "md:col-span-2" : ""}>
              <label className="field-label">{label}</label>
              <input className="field" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="field-label">About Text (shown on About page)</label>
            <textarea className="field" rows={5} value={form.about_text || ""} onChange={(e) => setForm({ ...form, about_text: e.target.value })} data-testid="settings-about-input" />
          </div>
          <div className="md:col-span-2 border-t border-slate-100 pt-5">
            <label className="field-label">Nextdoor Badge (optional — official badge/award image)</label>
            <p className="text-xs text-slate-400 mb-3">If Noyola Construction receives an official Nextdoor badge or recognition, upload the official image here and it will appear on the public Reviews page. Do not upload unverified awards.</p>
            <div className="flex items-center gap-4">
              {form.nextdoor_badge_url && <img src={imgUrl(form.nextdoor_badge_url)} alt="Nextdoor badge" className="h-16 w-auto object-contain border border-slate-200 p-1" data-testid="badge-preview" />}
              <button type="button" onClick={() => badgeRef.current?.click()} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="badge-upload-btn">
                <Upload className="w-4 h-4" /> {form.nextdoor_badge_url ? "Replace Badge" : "Upload Badge"}
              </button>
              {form.nextdoor_badge_url && (
                <button type="button" onClick={async () => {
                  const updated = { ...form, nextdoor_badge_url: "" };
                  setForm(updated);
                  const res = await api.put("/admin/settings", updated);
                  setSettings(res.data);
                }} className="btn-outline-dark !py-2.5 !px-4 !text-xs" data-testid="badge-remove-btn"><Trash2 className="w-4 h-4" /> Remove</button>
              )}
              <input ref={badgeRef} type="file" accept=".jpg,.jpeg,.png,.webp,.svg" className="hidden" data-testid="badge-file-input"
                onChange={(e) => uploadBadge(e.target.files[0])} />
            </div>
          </div>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary mt-8" data-testid="settings-save-btn">{busy ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}
